import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronsDownUp, ChevronsUpDown, Search } from "lucide-react";
import { Tree as LibTree, TreeNode as LibTreeNode } from "react-organizational-chart";
import { Button } from "../components/ui/button.js";
import { GlowCard } from "../components/ui/glow-card.js";
import { DropdownSelect } from "../components/DropdownButton.js";
import { SegmentedTabs } from "../components/SegmentedTabs.js";
import { SpecularButton } from "../components/ui/specular-button.js";
import { ZoomCanvas, type ZoomCanvasHandle } from "../components/ui/zoom-canvas.js";
import { api } from "../lib/api.js";
import { usePageBack } from "../context/PageBackContext.js";
import { useAuth } from "../hooks/useAuth.js";
import { cn } from "../lib/utils.js";
import { HR_ROLES, MGR_ROLES } from "@hrms/types";

function decodeAuth(token: string): { role: string; employeeId: string } {
  try {
    const p = JSON.parse(atob(token.split(".")[1]!)) as Record<string, unknown>;
    return { role: String(p["role"] ?? ""), employeeId: String(p["employeeId"] ?? "") };
  } catch {
    return { role: "", employeeId: "" };
  }
}

// ─── Types ────────────────────────────────────────────────────────────────────

interface FlatNode {
  id: string;
  firstName: string;
  lastName: string;
  employeeCode: string;
  designation: string;
  status: string;
  managerId: string | null;
  reportingManagerId: string | null;
  workLocation: string | null;
  department: { id: string; name: string } | null;
  office: { name: string; city: string } | null;
}

interface OrgNode extends FlatNode {
  children: OrgNode[];
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function buildTree(flat: FlatNode[], parentKey: "managerId" | "reportingManagerId" = "managerId"): OrgNode[] {
  const idSet = new Set(flat.map(n => n.id));
  const byParent = new Map<string | null, FlatNode[]>();
  for (const n of flat) {
    const pid = n[parentKey];
    const parent = pid && idSet.has(pid) ? pid : null;
    const list = byParent.get(parent) ?? [];
    list.push(n);
    byParent.set(parent, list);
  }
  function build(parentId: string | null): OrgNode[] {
    return (byParent.get(parentId) ?? []).map(n => ({
      ...n,
      children: build(n.id),
    }));
  }
  return build(null);
}

function avatarColor(name: string): string {
  const code = name.charCodeAt(0) ?? 65;
  return `hsl(${(code * 47) % 360}, 55%, 42%)`;
}

function initials(firstName: string, lastName: string): string {
  return `${firstName[0] ?? ""}${lastName[0] ?? ""}`.toUpperCase();
}

function statusDot(status: string): { className: string; label: string } {
  if (status === "ACTIVE")        return { className: "text-[var(--color-success-accent)]", label: "Active" };
  if (status === "ON_LEAVE")      return { className: "text-[var(--color-warning-accent)]", label: "On Leave" };
  if (status === "NOTICE_PERIOD") return { className: "text-[var(--color-warning-accent)]", label: "Notice" };
  return { className: "text-[var(--color-text-400)]", label: status };
}

function collectDepth(nodes: OrgNode[], maxDepth: number, current = 0): Set<string> {
  const ids = new Set<string>();
  if (current >= maxDepth) return ids;
  for (const n of nodes) {
    ids.add(n.id);
    collectDepth(n.children, maxDepth, current + 1).forEach(id => ids.add(id));
  }
  return ids;
}

function collectAncestors(nodes: OrgNode[], targetIds: Set<string>): Set<string> {
  const ids = new Set<string>();
  function walk(n: OrgNode, ancestorIds: string[]) {
    if (targetIds.has(n.id)) ancestorIds.forEach(id => ids.add(id));
    n.children.forEach(c => walk(c, [...ancestorIds, n.id]));
  }
  nodes.forEach(n => walk(n, []));
  return ids;
}

const VIEW_MODE_TABS = [
  { value: "hr", label: "HR Manager" },
  { value: "reporting", label: "Reporting Manager" },
];

// ─── NodeCard ─────────────────────────────────────────────────────────────────

interface NodeCardProps {
  node: OrgNode;
  isExpanded: boolean;
  hasChildren: boolean;
  isHighlighted: boolean;
  canOpen: boolean;
  setExpanded: (id: string, expand: boolean) => void;
}

function NodeCard({ node, isExpanded, hasChildren, isHighlighted, canOpen, setExpanded }: NodeCardProps) {
  const navigate = useNavigate();
  const { className: statusClass, label: statusLabel } = statusDot(node.status);

  return (
    <GlowCard
      data-match={isHighlighted ? "true" : undefined}
      glow="ultra"
      spotlight="low"
      size="none"
      width={168}
      className={cn(
        // inline-block: the tree library centres each label with text-align,
        // which a block-level card of fixed width would ignore.
        "inline-block select-none px-2.5 pb-2.5 pt-3 text-center transition-shadow",
        isHighlighted
          ? "border-2 border-brand-500 shadow-md ring-4 ring-brand-500/15 hover:shadow-lg"
          : "shadow-sm hover:shadow-md"
      )}
    >
      <div
        // One of 360 hues derived from the person's name — there is no static
        // class for a value computed per row.
        style={{ "--org-avatar-bg": avatarColor(`${node.firstName}${node.lastName}`) } as React.CSSProperties}
        className="org-node-avatar mx-auto mb-2 flex size-11 items-center justify-center rounded-full text-[15px] font-bold tracking-[0.5px] text-white"
      >
        {initials(node.firstName, node.lastName)}
      </div>

      <div
        className={cn(
          "truncate text-[13px] font-bold leading-[1.3] text-surface-900",
          canOpen && "cursor-pointer hover:underline"
        )}
        onClick={canOpen ? (e => { e.stopPropagation(); navigate(`/employees/${node.id}/edit`); }) : undefined}
      >
        {node.firstName} {node.lastName}
      </div>

      <div className="mt-[3px] truncate text-[11px] text-surface-600">
        {node.designation}
      </div>

      {node.department && (
        <div className="mt-px truncate text-[11px] text-surface-400">
          {node.department.name}
        </div>
      )}

      <div className={cn("mt-1.5 flex items-center justify-center gap-1 text-[10px]", statusClass)}>
        <span className="size-1.5 rounded-full bg-current" />
        {statusLabel}
      </div>

      {hasChildren && (
        <Button
          variant="secondary"
          size="sm"
          onClick={e => { e.stopPropagation(); setExpanded(node.id, !isExpanded); }}
          className={cn(
            "mt-2 h-auto w-full gap-1 rounded-full px-1.5 py-1 text-[10px]",
            isExpanded && "bg-brand-50 text-brand-700 hover:bg-brand-100"
          )}
        >
          {isExpanded
            ? <>▼ {node.children.length} {node.children.length === 1 ? "report" : "reports"}</>
            : <>▶ {node.children.length} direct {node.children.length === 1 ? "report" : "reports"}</>
          }
        </Button>
      )}
    </GlowCard>
  );
}

// ─── OrgChartNode (recursive, non-root) ──────────────────────────────────────

interface OrgChartNodeProps {
  node: OrgNode;
  expandedIds: Set<string>;
  matchingIds: Set<string>;
  canOpen: (node: OrgNode) => boolean;
  setExpanded: (id: string, expand: boolean) => void;
}

function OrgChartNode({ node, expandedIds, matchingIds, canOpen, setExpanded }: OrgChartNodeProps) {
  const showChildren = expandedIds.has(node.id);

  return (
    <LibTreeNode
      label={
        <NodeCard
          node={node}
          isExpanded={showChildren}
          hasChildren={node.children.length > 0}
          isHighlighted={matchingIds.has(node.id)}
          canOpen={canOpen(node)}
          setExpanded={setExpanded}
        />
      }
    >
      {showChildren && node.children.map(child => (
        <OrgChartNode
          key={child.id}
          node={child}
          expandedIds={expandedIds}
          matchingIds={matchingIds}
          canOpen={canOpen}
          setExpanded={setExpanded}
        />
      ))}
    </LibTreeNode>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function OrgChartPage() {
  usePageBack("/dashboard");
  const { accessToken } = useAuth();
  const { role: myRole, employeeId: myEmployeeId } = accessToken ? decodeAuth(accessToken) : { role: "", employeeId: "" };
  const canOpen = useCallback((node: OrgNode) =>
    node.id === myEmployeeId || HR_ROLES.has(myRole) || (MGR_ROLES.has(myRole) && node.managerId === myEmployeeId),
  [myRole, myEmployeeId]);
  const [flat, setFlat]             = useState<FlatNode[]>([]);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState("");
  const [search, setSearch]         = useState("");
  const [deptFilter, setDeptFilter] = useState("");
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [viewMode, setViewMode]     = useState<"hr" | "reporting">("hr");
  const canvasRef = useRef<ZoomCanvasHandle>(null);
  // One "which dropdown is open" flag, so adding a second filter here closes
  // the first automatically (DropdownSelect's controlled open/onToggle/onClose).
  const [openDropdown, setOpenDropdown] = useState<"dept" | null>(null);

  useEffect(() => {
    api.get<FlatNode[]>("/employees/org-chart")
      .then(r => {
        setFlat(r.data);
        const tree = buildTree(r.data, "managerId");
        setExpandedIds(collectDepth(tree, 1));
      })
      .catch(() => setError("Failed to load org chart."))
      .finally(() => setLoading(false));
  }, []);

  const parentKey = viewMode === "hr" ? "managerId" : "reportingManagerId";
  const tree = useMemo(() => buildTree(flat, parentKey), [flat, parentKey]);

  useEffect(() => {
    setExpandedIds(collectDepth(tree, 1));
  }, [parentKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const departments = useMemo(() => {
    const names = new Set<string>();
    flat.forEach(n => { if (n.department?.name) names.add(n.department.name); });
    return [...names].sort();
  }, [flat]);

  const departmentOptions = useMemo(
    () => departments.map(d => ({ value: d, label: d })),
    [departments]
  );

  const q = search.toLowerCase().trim();
  const isSearchActive = q.length > 0 || deptFilter.length > 0;

  const matchingIds = useMemo(() => {
    if (!isSearchActive) return new Set<string>();
    return new Set(flat.filter(n => {
      const deptMatch = !deptFilter || n.department?.name === deptFilter;
      const searchMatch = !q ||
        `${n.firstName} ${n.lastName}`.toLowerCase().includes(q) ||
        n.designation.toLowerCase().includes(q) ||
        (n.department?.name ?? "").toLowerCase().includes(q);
      return searchMatch && deptMatch;
    }).map(n => n.id));
  }, [flat, q, deptFilter, isSearchActive]);

  // Auto-expand ancestor path so matched nodes are visible, then scroll to first match
  useEffect(() => {
    if (!matchingIds.size) return;
    const ancestors = collectAncestors(tree, matchingIds);
    setExpandedIds(s => {
      const merged = new Set(s);
      ancestors.forEach(id => merged.add(id));
      return merged;
    });
    // The canvas pans by transform, so bring the first match into view by
    // moving the stage rather than scrolling an overflow container.
    const timer = setTimeout(() => {
      canvasRef.current?.centerOn(document.querySelector("[data-match='true']"));
    }, 150);
    return () => clearTimeout(timer);
  }, [matchingIds, tree]);

  const setExpanded = useCallback((id: string, expand: boolean) => {
    setExpandedIds(s => {
      const n = new Set(s);
      if (expand) n.add(id); else n.delete(id);
      return n;
    });
  }, []);

  const expandAll   = () => setExpandedIds(new Set(flat.map(n => n.id)));
  const collapseAll = () => setExpandedIds(new Set());
  const anyExpanded = expandedIds.size > 0;

  const inp: React.CSSProperties = {
    padding: "7px 10px", border: "1px solid var(--color-surface-200)",
    borderRadius: "var(--radius-md)", fontSize: "13px",
    background: "var(--color-surface-0)", outline: "none",
  };

  return (
    <div className="flex h-full min-h-0 flex-col bg-surface-50">
      {/* Header — Back lives in the top bar (usePageBack), never in the body. */}
      <div style={{
        background: "var(--color-surface-0)", borderBottom: "1px solid var(--color-surface-200)",
        padding: "var(--space-5) var(--space-8)", display: "flex", alignItems: "center", gap: 12,
      }}>
        <h1 style={{ margin: 0, fontSize: "18px", fontWeight: 700, color: "var(--color-surface-900)" }}>
          Org Hierarchy
        </h1>
        {!loading && (
          <span style={{ fontSize: "12px", color: "var(--color-surface-500)" }}>
            {flat.length} employees
          </span>
        )}
      </div>

      {/* Filter bar */}
      <div style={{
        background: "var(--color-surface-0)", borderBottom: "1px solid var(--color-surface-200)",
        padding: "var(--space-3) var(--space-8)", display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap",
      }}>
        <div style={{ position: "relative", flex: "1 1 220px", maxWidth: 300 }}>
          <span style={{ position: "absolute", left: 9, top: "50%", transform: "translateY(-50%)", display: "inline-flex", color: "var(--color-surface-400)", pointerEvents: "none" }}><Search size={13} /></span>
          <input
            style={{ ...inp, width: "100%", paddingLeft: 30, boxSizing: "border-box" }}
            placeholder="Search name, title, department…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>

        <DropdownSelect
          value={deptFilter}
          onChange={setDeptFilter}
          options={departmentOptions}
          placeholder="All Departments"
          ariaLabel="Department"
          className="min-w-[160px]"
          open={openDropdown === "dept"}
          onToggle={() => setOpenDropdown(o => (o === "dept" ? null : "dept"))}
          onClose={() => setOpenDropdown(null)}
        />

        {isSearchActive && (
          <span style={{ fontSize: 12, color: matchingIds.size ? "var(--color-brand-600)" : "var(--color-surface-400)", fontWeight: 600 }}>
            {matchingIds.size} {matchingIds.size === 1 ? "match" : "matches"}
          </span>
        )}

        <div style={{ display: "flex", gap: 6, marginLeft: "auto", alignItems: "center" }}>
          <SegmentedTabs
            items={VIEW_MODE_TABS}
            value={viewMode}
            onChange={v => setViewMode(v as "hr" | "reporting")}
            size="sm"
          />

          {/* One button, not two: the only useful bulk action is whichever one
              the tree isn't already in. h-9 lines it up with the dropdown. */}
          <SpecularButton
            className="h-9 font-semibold"
            onClick={anyExpanded ? collapseAll : expandAll}
          >
            {anyExpanded
              ? <><ChevronsDownUp size={14} /> Collapse All</>
              : <><ChevronsUpDown size={14} /> Expand All</>}
          </SpecularButton>
        </div>
      </div>

      {/* Status states sit outside the canvas: the canvas stage is sized to its
          content, so a message inside it could not centre on the viewport. */}
      {(loading || error || tree.length === 0) ? (
        <div className="flex min-h-0 flex-1 flex-col items-center justify-center px-8">
          {error && (
            <div style={{ background: "var(--color-error-bg)", color: "var(--color-error-fg)", padding: "10px 14px", borderRadius: "var(--radius-md)", marginBottom: 12, fontSize: 13 }}>
              {error}
            </div>
          )}

          {loading && (
            <div style={{ textAlign: "center", padding: "var(--space-10)", color: "var(--color-surface-500)" }}>
              Loading…
            </div>
          )}

          {!loading && tree.length === 0 && !error && (
            <div style={{ textAlign: "center", padding: "var(--space-10)", color: "var(--color-surface-400)", fontSize: 14 }}>
              No employees found.
            </div>
          )}
        </div>
      ) : (
      /* Tree canvas — drag to pan, ctrl/⌘ + wheel or pinch to zoom */
      <ZoomCanvas ref={canvasRef} className="min-h-0 flex-1" contentClassName="px-8">
        {tree.map(root => {
          const rootExpanded = expandedIds.has(root.id);

          return (
            <div key={root.id} style={{ marginBottom: 48 }}>
              <LibTree
                lineWidth="2px"
                lineColor="var(--color-surface-300)"
                lineBorderRadius="6px"
                label={
                  <NodeCard
                    node={root}
                    isExpanded={rootExpanded}
                    hasChildren={root.children.length > 0}
                    isHighlighted={matchingIds.has(root.id)}
                    canOpen={canOpen(root)}
                    setExpanded={setExpanded}
                  />
                }
              >
                {rootExpanded && root.children.map(child => (
                  <OrgChartNode
                    key={child.id}
                    node={child}
                    expandedIds={expandedIds}
                    matchingIds={matchingIds}
                    canOpen={canOpen}
                    setExpanded={setExpanded}
                  />
                ))}
              </LibTree>
            </div>
          );
        })}
      </ZoomCanvas>
      )}
    </div>
  );
}
